import { Link } from "@tanstack/react-router"
import type { ProductsListItem } from "@nova-ai/shared"
import { Boxes, CircleAlert, ImageOff, Loader2 } from "lucide-react"
import { useCallback, useEffect, useId, useRef, useState } from "react"

import { Button } from "../ui/button"
import { Callout } from "../ui/callout"
import { StatusBadge, type BadgeTone } from "../ui/status-badge"
import { humanizeEnumLabel } from "../../lib/enum-label"

type ProductsGridProps = {
  products: ProductsListItem[]
  isLoading: boolean
  error: unknown
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => Promise<unknown>
  onClearFilters: () => void
  /** Optional context for an empty marketplace-state view. */
  emptyMessage?: string
}

export function ProductsGrid({
  products,
  isLoading,
  error,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onClearFilters,
  emptyMessage,
}: ProductsGridProps) {
  const isNextPageRequestLocked = useRef(false)
  const requestNextPage = useCallback(() => {
    if (isNextPageRequestLocked.current) {
      return
    }
    isNextPageRequestLocked.current = true
    const unlockRequest = () => {
      isNextPageRequestLocked.current = false
    }
    void onLoadMore().then(unlockRequest, unlockRequest)
  }, [onLoadMore])

  if (isLoading) {
    return <ProductsGridSkeleton />
  }
  if (error && products.length === 0) {
    return (
      <Callout
        tone="destructive"
        icon={<CircleAlert className="size-4" />}
        title="Products could not be loaded"
      >
        <p className="mt-2 text-sm text-foreground/80">{errorMessage(error)}</p>
      </Callout>
    )
  }
  if (products.length === 0) {
    return (
      <div className="flex min-h-64 flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card px-6 py-12 text-center">
        <span className="inline-flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Boxes className="size-4" />
        </span>

        <h2 className="mt-3 text-sm font-medium text-foreground">No matching products</h2>

        <p className="mt-1 max-w-md text-sm text-muted-foreground">
          {emptyMessage ?? "Try another product name, config SKU, variant SKU, or category."}
        </p>

        <Button className="mt-4" size="sm" onClick={onClearFilters}>
          Clear filters
        </Button>
      </div>
    )
  }

  return (
    <div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {products.map((product) => (
          <ProductTile key={product.configSku} product={product} />
        ))}
      </div>

      {error ? (
        <Callout
          role="alert"
          className="mt-6"
          tone="destructive"
          icon={<CircleAlert className="size-4" />}
          title="More products could not be loaded"
        >
          <p className="mt-2 text-sm text-foreground/80">{errorMessage(error)}</p>

          <Button
            className="mt-3"
            size="sm"
            onClick={requestNextPage}
            disabled={isFetchingNextPage}
          >
            Retry
          </Button>
        </Callout>
      ) : null}

      <ProductsTail
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        error={error}
        onLoadMore={requestNextPage}
      />
    </div>
  )
}

function ProductsTail({
  hasNextPage,
  isFetchingNextPage,
  error,
  onLoadMore,
}: {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  error: unknown
  onLoadMore: () => void
}) {
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isFetchingNextPage || !hasNextPage || Boolean(error)) {
      return
    }
    const sentinel = sentinelRef.current
    if (!sentinel) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return
        }
        onLoadMore()
      },
      { rootMargin: "400px 0px" }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [error, hasNextPage, isFetchingNextPage, onLoadMore])

  if (!hasNextPage && !isFetchingNextPage) {
    return null
  }

  return (
    <div className="flex min-h-12 items-center justify-center pt-4">
      {isFetchingNextPage ? (
        <div
          role="status"
          aria-label="Loading more products"
          className="inline-flex items-center gap-2 text-xs text-muted-foreground"
        >
          <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />
          Loading more…
        </div>
      ) : null}

      {hasNextPage && !error ? <div ref={sentinelRef} className="h-px w-full" aria-hidden /> : null}
    </div>
  )
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unknown error"
}

function ProductTile({ product }: { product: ProductsListItem }) {
  const [hasImageError, setHasImageError] = useState(false)
  const marketplaceStateDescriptionId = useId()
  const trimmedName = product.name?.trim()
  const productName =
    trimmedName === undefined || trimmedName.length === 0 ? "Unnamed product" : trimmedName
  const hasImage = Boolean(product.mainImage && !hasImageError)
  const hasStateDescription = Boolean(product.selectedMarketplace ?? !product.isCatalogLive)

  return (
    <Link
      to="/products/$sku"
      params={{ sku: product.configSku }}
      className="group overflow-hidden rounded-xl border border-border bg-card shadow-card outline-none transition-[border-color,transform] duration-200 ease-[var(--ease-product-out)] hover:-translate-y-0.5 hover:border-foreground/20 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transform-none"
      aria-label={`Open ${productName}, SKU ${product.configSku}`}
      aria-describedby={hasStateDescription ? marketplaceStateDescriptionId : undefined}
    >
      <div className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-muted/35 p-5">
        {hasImage ? (
          <img
            src={product.mainImage ?? ""}
            alt=""
            loading="lazy"
            onError={() => setHasImageError(true)}
            className="size-full object-contain transition-transform duration-200 ease-[var(--ease-product-out)] group-hover:scale-[1.025] motion-reduce:transform-none"
          />
        ) : (
          <ImageOff className="size-7 text-muted-foreground/50" aria-hidden="true" />
        )}
      </div>

      <div className="border-t border-border/70 p-4">
        <ProductStateBadges
          product={product}
          descriptionId={marketplaceStateDescriptionId}
          visible={hasStateDescription}
        />

        <h2 className="line-clamp-2 min-h-10 text-sm font-semibold leading-5 text-foreground">
          {productName}
        </h2>

        {product.selectedMarketplace ? (
          <p className="mt-2 text-xs tabular-nums text-muted-foreground">
            {product.offerActivity
              ? `Offers ${product.offerActivity.matchedOfferCount}/${product.offerActivity.enabledVariantCount} · ${product.offerActivity.activeOfferCount} active · ${unknownOfferCountLabel(product.offerActivity.unknownOfferCount)}`
              : "Offers Unknown"}
          </p>
        ) : null}

        <div className="mt-3 flex items-end justify-between gap-3">
          <span className="truncate font-mono text-xs text-muted-foreground">
            {product.configSku}
          </span>

          <span className="shrink-0 text-xs text-muted-foreground">
            {categoryLabel(product.masterCategory)}
          </span>
        </div>
      </div>
    </Link>
  )
}

function ProductStateBadges({
  product,
  descriptionId,
  visible,
}: {
  product: ProductsListItem
  descriptionId: string
  visible: boolean
}) {
  if (!visible) {
    return null
  }
  return (
    <>
      <div className="mb-2 flex flex-wrap gap-1.5" aria-hidden="true">
        {!product.isCatalogLive ? (
          <StatusBadge tone="destructive" size="sm">
            Catalog inactive
          </StatusBadge>
        ) : null}

        {product.selectedMarketplace ? (
          <StatusBadge tone={MARKETPLACE_STATE_TONE[product.selectedMarketplace.state]} size="sm">
            {MARKETPLACE_STATE_LABEL[product.selectedMarketplace.state]}
          </StatusBadge>
        ) : null}

        {product.selectedMarketplace?.isStale ? (
          <StatusBadge tone="warning" size="sm">
            Stale
          </StatusBadge>
        ) : null}

        {product.selectedMarketplace?.isSetAside ? (
          <StatusBadge size="sm">Set aside</StatusBadge>
        ) : null}

        {product.selectedMarketplace?.isDelisted ? (
          <StatusBadge tone="destructive" size="sm">
            Listing inactive
          </StatusBadge>
        ) : null}

        {product.offerActivity ? (
          <StatusBadge tone={OFFER_STATE_TONE[product.offerActivity.state]} size="sm">
            Offer {OFFER_STATE_LABEL[product.offerActivity.state]}
          </StatusBadge>
        ) : null}
      </div>

      <span id={descriptionId} className="sr-only">
        {productStateDescription(product)}
      </span>
    </>
  )
}

const MARKETPLACE_STATE_TONE = {
  ok: "success",
  nok: "destructive",
  pending: "muted",
} as const satisfies Record<"ok" | "nok" | "pending", BadgeTone>

const MARKETPLACE_STATE_LABEL = {
  ok: "OK",
  nok: "NOK",
  pending: "Pending",
} as const satisfies Record<"ok" | "nok" | "pending", string>

const OFFER_STATE_TONE = {
  active: "success",
  partial: "warning",
  inactive: "destructive",
  missing: "muted",
  unknown: "muted",
} as const satisfies Record<"active" | "partial" | "inactive" | "missing" | "unknown", BadgeTone>

const OFFER_STATE_LABEL = {
  active: "Active",
  partial: "Partial",
  inactive: "Inactive",
  missing: "Missing",
  unknown: "Unknown",
} as const

function unknownOfferCountLabel(count: number | undefined): string {
  return count === undefined ? "unknown count unavailable" : `${count} unknown`
}

function productStateDescription(product: ProductsListItem): string {
  const catalog = `Catalog state: ${product.isCatalogLive ? "Live" : "Inactive"}.`
  const state = product.selectedMarketplace
  if (!state) {
    return catalog
  }
  const labels = [
    MARKETPLACE_STATE_LABEL[state.state],
    ...(state.isStale ? ["Stale"] : []),
    ...(state.isSetAside ? ["Set aside"] : []),
    ...(state.isDelisted ? ["Listing inactive"] : []),
  ]
  const marketplace = `Marketplace state: ${labels.join(", ")}.`
  if (!product.offerActivity) {
    return `${catalog} ${marketplace} Offer state: Unknown.`
  }
  return `${catalog} ${marketplace} Offer state: ${OFFER_STATE_LABEL[product.offerActivity.state]}, ${product.offerActivity.matchedOfferCount} of ${product.offerActivity.enabledVariantCount} offers matched, ${product.offerActivity.activeOfferCount} active, ${unknownOfferCountLabel(product.offerActivity.unknownOfferCount)}.`
}

function categoryLabel(category: string | null): string {
  if (!category) {
    return "Uncategorized"
  }
  return humanizeEnumLabel(category)
}

function ProductsGridSkeleton() {
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      role="status"
      aria-label="Loading products"
    >
      {Array.from({ length: 8 }, (_, index) => (
        <div
          key={index}
          className="overflow-hidden rounded-xl border border-border bg-card shadow-card"
        >
          <div className="aspect-[4/3] animate-pulse bg-muted/60 motion-reduce:animate-none" />

          <div className="space-y-3 p-4">
            <div className="h-4 w-4/5 animate-pulse rounded-full bg-muted motion-reduce:animate-none" />

            <div className="h-3 w-2/5 animate-pulse rounded-full bg-muted motion-reduce:animate-none" />
          </div>
        </div>
      ))}
    </div>
  )
}
